export interface ArticleBlock {
  type: "heading" | "paragraph" | "quote" | "list";
  text?: string;
  items?: string[];
}

export interface Article {
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  cover: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
  views: number;
  featured?: boolean;
  blocks: ArticleBlock[];
}

export const articles: Article[] = [
  {
    slug: "tunguska-1908-explosion",
    title: "1908 通古斯大爆炸：被抹去的火球与两千平方公里倒下的森林",
    category: "unsolved",
    excerpt:
      "没有陨石坑，没有碎片，却有两千平方公里的森林整齐倒下。一百多年过去，那团划过西伯利亚上空的东西，依然没有一个所有人都接受的答案。",
    cover:
      "https://readdy.ai/api/search-image?query=Dark%20desolate%20Siberian%20taiga%20forest%20with%20thousands%20of%20flattened%20fallen%20trees%20radiating%20outward%2C%20misty%20dusk%20light%2C%20charcoal%20and%20ash%20palette%20with%20faint%20amber%20glow%20on%20horizon%2C%20cinematic%20grainy%20photograph%20mood%2C%20no%20people&width=1200&height=800&seq=lieqi-tunguska-01&orientation=landscape",
    author: "档案室老周",
    date: "2026-03-18",
    readTime: "9 分钟",
    tags: ["西伯利亚", "天体撞击", "历史悬案"],
    views: 48210,
    featured: true,
    blocks: [
      {
        type: "paragraph",
        text: "1908 年 6 月 30 日清晨，鄂温克牧民看见一颗比太阳更亮的火球拖着长尾划过天空，随后是连几百公里外都能感到的震波。那一刻之后，通古斯河附近的森林以火球落点为中心，向外呈放射状整片倒下。",
      },
      {
        type: "heading",
        text: "现场：一片站着的树，围着倒下的树",
      },
      {
        type: "paragraph",
        text: "最早抵达的考察队发现了一件怪事：爆炸正下方的树还立着，但树皮被剥光、枝叶全无，而外围的树像被同一只手推向四面。没有陨石坑，没有大块残骸，土壤里只有异常浓度的金属微粒。",
      },
      {
        type: "quote",
        text: "如果是一颗陨石，它应该在冰河里留下一个坑；可它留下的，是一整片保持站姿的焦木。",
      },
      {
        type: "heading",
        text: "主流的推测与被反复推翻的解释",
      },
      {
        type: "list",
        items: [
          "空中解体说：小行星或彗星在数公里高空爆炸，未触及地面，是最被广泛接受的模型。",
          "彗星冰核说：冰质天体进入大气后迅速蒸发，因此几乎不留固体残骸。",
          "自然核反应说：极少数研究者猜测是地下气体的天然爆炸，但缺乏关键证据。",
          "更激进的假说：反物质、微型黑洞等，多停留在理论层面，无法验证。",
        ],
      },
      {
        type: "paragraph",
        text: "真正让档案无法合上的，是细节对不上：金属微粒的分布、树木倒向的对称性、以及同期全球夜空的异常亮光。每当你觉得答案清晰了一点，总有一个数据把你拉回原点。",
      },
      {
        type: "heading",
        text: "为什么我们还在谈论它",
      },
      {
        type: "paragraph",
        text: "通古斯之所以迷人，不是因为它神秘到无法解释，而是因为它差一点点就能被解释。它站在「已解」与「未解」之间那道最窄的缝里——也正因如此，一百多年后，它依然是我们翻开悬案卷宗时最先看到的那一页。",
      },
    ],
  },
  {
    slug: "mary-celeste-ghost-ship",
    title: "玛丽·塞莱斯特号：全员消失之后，那顿早餐还摆在桌上",
    category: "archive",
    excerpt:
      "船完好、货物完好、九个人的私人物品都在，唯独人不见了。这艘船漂在大西洋上，像一件被翻到一半就合上的旧案卷。",
    cover:
      "https://readdy.ai/api/search-image?query=Lone%20abandoned%20sailing%20ship%20drifting%20on%20calm%20dark%20ocean%20under%20heavy%20overcast%20sky%2C%20eerily%20empty%20deck%2C%20muted%20charcoal%20grey%20sea%20with%20dull%20amber%20horizon%20light%2C%20atmospheric%20cinematic%20photograph%2C%20fine%20grain%2C%20no%20people&width=1200&height=800&seq=lieqi-maryceleste-01&orientation=landscape",
    author: "夜航员",
    date: "2026-02-27",
    readTime: "8 分钟",
    tags: ["海上悬案", "幽灵船", "1872"],
    views: 39564,
    featured: true,
    blocks: [
      {
        type: "paragraph",
        text: "1872 年 12 月，一艘英国商船在亚速尔群岛以东的洋面上，发现了一艘航行姿态异常的帆船。它没有挂旗，没有回应呼喊，只是顺着洋流缓慢漂移。登船之后，人们看到的是一艘「正在使用中」的船——只是船上没有人。",
      },
      {
        type: "heading",
        text: "完好无损的现场",
      },
      {
        type: "list",
        items: [
          "货物中的工业酒精全部原封未动，排除了海盗劫掠的可能。",
          "船员的水手袋、衣物、烟斗都留在各自舱室，不像仓促撤离。",
          "罗盘、部分航海仪器与救生艇不见踪影。",
          "厨房里仍摆着未吃完的一餐，像被突然打断的早餐。",
        ],
      },
      {
        type: "quote",
        text: "一只救生艇被整齐地放下，又被留在了不知道哪里的海面上。",
      },
      {
        type: "heading",
        text: "几种被讨论最多的推测",
      },
      {
        type: "paragraph",
        text: "最常被引用的是货物渗漏引发恐慌说：工业酒精桶在海上受潮，船长担心蒸汽积聚引发爆燃，于是下令全员乘救生艇暂避，结果小艇被风浪推离母船，再也没能回来。这个解释合理，却无法回答一个细节——为什么小艇上没有任何一个人留下求救的痕迹。",
      },
      {
        type: "paragraph",
        text: "另一种说法指向海水意外：有人落水，船长放下小艇施救，风向突变导致帆船加速离开。还有更大胆的版本——风暴、海龙卷、甚至是集体失踪。档案里写满了可能性，却始终缺一行决定性结论。",
      },
      {
        type: "heading",
        text: "它为什么成为经典悬案",
      },
      {
        type: "paragraph",
        text: "玛丽·塞莱斯特号之所以反复被讲述，是因为它把一个「完整的现场」留给了后世，却没有留下任何一个人的证词。你能看见他们生活过的一切，唯独看不见他们去了哪里。",
      },
    ],
  },
  {
    slug: "hinterkaifeck-farm-murders",
    title: "辛特凯菲克农场血案：凶手在屋里住了三天，还喂了牛",
    category: "archive",
    excerpt:
      "一家人被杀害，凶手却没有立刻离开。他在屋里生火做饭、喂牲口，像什么都没发生过一样住了整整三天。",
    cover:
      "https://readdy.ai/api/search-image?query=Desolate%20old%20isolated%20farmhouse%20in%20winter%20Germany%20with%20bare%20trees%20and%20dense%20fog%2C%20snow%20on%20the%20ground%2C%20single%20dim%20window%20light%2C%20cold%20desaturated%20charcoal%20tones%20with%20faint%20rust%20browns%2C%20ominous%20cinematic%20atmosphere%2C%20no%20people&width=1200&height=800&seq=lieqi-hinterkaifeck-01&orientation=landscape",
    author: "灯下客",
    date: "2026-01-14",
    readTime: "10 分钟",
    tags: ["欧洲旧案", "乡村悬案", "1922"],
    views: 33120,
    blocks: [
      {
        type: "paragraph",
        text: "1922 年的德国巴伐利亚，辛特凯菲克农场偏僻得几乎与世隔绝。农场主一家五口加上女佣，在一夜之间全部遇害。真正让这案子无法被人忘记的，是凶案之后的细节——烟囱还冒着烟。",
      },
      {
        type: "heading",
        text: "被时间线出卖的凶手",
      },
      {
        type: "list",
        items: [
          "案发后，屋内的炉火被继续使用，谷仓里的牛被照常喂养。",
          "邻居在几天后才发现异常，此前曾看见农场烟囱冒烟。",
          "有目击者称，案发前后在农场附近见过一个陌生男子。",
        ],
      },
      {
        type: "quote",
        text: "他不是仓皇逃走的凶手，他是在这栋房子里安顿下来的凶手。",
      },
      {
        type: "heading",
        text: "此前已经出现过的预警",
      },
      {
        type: "paragraph",
        text: "更令人不安的是，案发前几日，农场主曾告诉邻居，雪地里出现了从森林通往农场的脚印，却没有任何回程的痕迹；他还说夜里听到阁楼有响动，并发现了一串不属于家里任何人的旧钥匙。这些线索在案发后才被重新想起，却已经无法指认任何人。",
      },
      {
        type: "heading",
        text: "为什么至今无人被定罪",
      },
      {
        type: "paragraph",
        text: "调查中出现了大量可疑人物，却全部因证据不足或不在场证明被排除。凶手在屋里停留的时间、对农场生活的熟悉程度，暗示他可能早就潜伏其中，也可能就是村里某个被信任的人。卷宗被一次次翻出，又一次次放回铁柜。",
      },
    ],
  },
  {
    slug: "sleep-paralysis-shadow-man",
    title: "睡眠瘫痪里的黑影人：为什么全世界都梦到了同一张脸",
    category: "supernatural",
    excerpt:
      "凌晨三点醒来，身体动不了，胸口压着重量，门口站着一个影子。奇怪的是，不同的文化给出了几乎相同的描述。",
    cover:
      "https://readdy.ai/api/search-image?query=Dim%20bedroom%20interior%20at%20night%20seen%20from%20a%20mattress%20level%20viewpoint%2C%20soft%20moonlight%20through%20curtains%2C%20tall%20blurred%20shadow%20figure%20silhouette%20in%20the%20doorway%2C%20deep%20charcoal%20blues%20and%20warm%20amber%20lamp%20glow%2C%20surreal%20cinematic%20mood%2C%20grainy%20texture%2C%20no%20faces&width=1200&height=800&seq=lieqi-shadowman-01&orientation=landscape",
    author: "白噪点",
    date: "2026-04-02",
    readTime: "7 分钟",
    tags: ["睡眠瘫痪", "集体幻象", "神经科学"],
    views: 51980,
    featured: true,
    blocks: [
      {
        type: "paragraph",
        text: "几乎每一种文化都有一张属于自己的「夜里压胸的东西」：中国的鬼压床、加拿大的老巫婆、巴西的阴影访客、北欧的梦魇。把它们摆在一起你会发现，描述撞得惊人地一致——动不了、听得见、看得见一个站在房间里的身影。",
      },
      {
        type: "heading",
        text: "身体醒了，大脑还没醒",
      },
      {
        type: "paragraph",
        text: "睡眠瘫痪发生在快速眼动睡眠阶段：大脑的活动接近清醒，肌肉却仍处于「瘫痪保护」状态。此时若意识提前浮出水面，人就会体验到自己醒来、却无法控制身体。脑干同时可能残留梦境内容，于是卧室被叠加上了不存在的影像。",
      },
      {
        type: "quote",
        text: "恐惧不是被看见的东西带来的，而是身体无法回应时，大脑自己补全的剧本。",
      },
      {
        type: "heading",
        text: "为什么会是「那个影子」",
      },
      {
        type: "list",
        items: [
          "人在昏暗环境里倾向于把模糊轮廓识别为人的形状——这是视觉系统的默认倾向。",
          "胸口的压迫感来自呼吸肌未完全恢复，却被大脑解释为「有人压着」。",
          "恐惧会激活威胁识别回路，房间里的阴影被进一步「加工」成人物。",
        ],
      },
      {
        type: "heading",
        text: "为什么它依然让人着迷",
      },
      {
        type: "paragraph",
        text: "神经科学能解释机制，却解释不了那种体验本身的强烈真实感。很多人一生只经历一次，却能在十几年后精准复述房间的布局、影子的位置和当时的心跳。它像一次来自内部的越界，提醒我们：最陌生的东西有时就住在自己身体里。",
      },
    ],
  },
  {
    slug: "kuchisake-onna-urban-legend",
    title: "裂口女：一条传说如何从校园走廊走进整个日本的童年",
    category: "urban",
    excerpt:
      "口罩、长发、一把剪刀，和一个必须回答的问题。你回答得慢一点，结局就会改写。",
    cover:
      "https://readdy.ai/api/search-image?query=Empty%20dim%20Japanese%20school%20corridor%20at%20dusk%20with%20long%20shadows%20and%20shuttered%20classroom%20doors%2C%20distant%20blurred%20lone%20figure%20in%20a%20long%20coat%20silhouetted%20at%20the%20end%20of%20the%20hallway%2C%20muted%20sepia%20and%20charcoal%20palette%20with%20faint%20rust%20red%20accent%20light%2C%20cinematic%20horror%20photography%2C%20no%20visible%20face&width=1200&height=800&seq=lieqi-kuchisake-01&orientation=landscape",
    author: "匿名猫",
    date: "2026-05-21",
    readTime: "6 分钟",
    tags: ["日本都市传说", "校园怪谈", "口述史"],
    views: 44370,
    blocks: [
      {
        type: "paragraph",
        text: "1979 年前后，「裂口女」的传说像一场草稿式的流行病席卷日本。它不是从电影开始的，而是从放学路上、从同学之间的低语开始，最后大到让一些小学提前放学。",
      },
      {
        type: "heading",
        text: "那个必须回答的问题",
      },
      {
        type: "quote",
        text: "我漂亮吗？",
      },
      {
        type: "paragraph",
        text: "传说里，她会问路上遇到的孩子这个问题。回答方式决定了不同的版本结局——这也是它最狡猾的地方：传说本身把玩家变成了编剧。不同的城市流传着不同的「正确答案」，从回答「一般」到撒糖果，各有各的地方智慧。",
      },
      {
        type: "heading",
        text: "传说为什么会扩散得那么快",
      },
      {
        type: "list",
        items: [
          "门槛极低：只需要一个口罩、一句问话，任何人都能立刻复述并传播。",
          "参与感强：回答方式可自定义，让每个复述者都成为共同作者。",
          "媒介助推：当年的报纸、周刊与后来的动画电影，把它从校园推向了全国。",
        ],
      },
      {
        type: "heading",
        text: "它真正留下的东西",
      },
      {
        type: "paragraph",
        text: "今天回看，裂口女更像一面镜子：它照出的是那个年代大人无暇顾及、孩子只能靠传说自我调节的放学时光。传说会淡去，但那个站在走廊尽头、等你回答问题的影子，一直留在很多人的记忆里。",
      },
    ],
  },
  {
    slug: "elevator-game-fourth-floor",
    title: "电梯游戏：按下那串楼层之后，四楼会多出一个女人",
    category: "urban",
    excerpt:
      "一套据说只能在特定顺序下进行的电梯操作。有人说是游戏，有人说这是一次邀请。",
    cover:
      "https://readdy.ai/api/search-image?query=Old%20cramped%20apartment%20elevator%20interior%20with%20brushed%20metal%20panel%20and%20glowing%20numbered%20buttons%2C%20flickering%20fluorescent%20ceiling%20light%2C%20reflection%20on%20the%20mirror%20wall%2C%20dark%20charcoal%20shadows%20with%20amber%20warmth%20from%20the%20panel%2C%20unsettling%20cinematic%20photograph%2C%20no%20people&width=1200&height=800&seq=lieqi-elevator-01&orientation=landscape",
    author: "匿名猫",
    date: "2026-06-08",
    readTime: "6 分钟",
    tags: ["电梯游戏", "仪式类传说", "网络怪谈"],
    views: 36740,
    blocks: [
      {
        type: "paragraph",
        text: "在都市传说的谱系里，「电梯游戏」属于仪式型怪谈：它给你一套明确的操作步骤，声称只要你照做，楼里就会多出一个本不存在的人。它的魅力不在于结果，而在于那套几乎像真的步骤。",
      },
      {
        type: "heading",
        text: "一套口耳相传的操作",
      },
      {
        type: "list",
        items: [
          "独自进入一栋有多层楼的建筑，最好是有十层以上的老楼。",
          "按顺序前往四个指定楼层，每一步都不要走出电梯。",
          "据说到某一个楼层时，会上来一位你从未见过的女性乘客。",
          "千万不要在此时与她对视，也不要询问她要去的楼层。",
        ],
      },
      {
        type: "quote",
        text: "规则写得越具体，就越像有人真的经历过——这是所有仪式型传说的共同语法。",
      },
      {
        type: "heading",
        text: "它为什么能一直流传",
      },
      {
        type: "paragraph",
        text: "电梯本身就是一处理想的舞台：封闭、重复、电梯门每次打开都像一次幕布。它把「不可控」包装成了一套可执行的动作，让人产生一种错觉——只要照着做，就能摸到另一个世界。至于那层多出来的楼层，多半只是老式电梯里被跳过编号的现实，被传说借走做了道具。",
      },
    ],
  },
  {
    slug: "moscow-metro-1975-vanished-train",
    title: "1975 莫斯科地铁失踪列车：一辆载着乘客的车，消失在两站之间",
    category: "unsolved",
    excerpt:
      "调度记录显示它离开了上一站，却始终没有抵达下一站。四十多年过去，没有人能给出让所有人信服的解释。",
    cover:
      "https://readdy.ai/api/search-image?query=Deep%20underground%20Soviet%20era%20metro%20tunnel%20stretching%20into%20total%20darkness%2C%20single%20dim%20amber%20maintenance%20lamp%2C%20wet%20concrete%20walls%20and%20rusting%20rails%2C%20cold%20charcoal%20grey%20palette%2C%20claustrophobic%20cinematic%20atmosphere%2C%20faint%20lens%20flare%2C%20no%20people&width=1200&height=800&seq=lieqi-metro-01&orientation=landscape",
    author: "档案室老周",
    date: "2026-07-19",
    readTime: "7 分钟",
    tags: ["地铁", "集体失踪", "冷战传闻"],
    views: 41080,
    blocks: [
      {
        type: "paragraph",
        text: "这个传说常被讲成「一列载满乘客的地铁在两站之间凭空消失」。版本很多，细节却反复出现：深夜、值班员、异常信号、以及一段被临时封闭的区间。",
      },
      {
        type: "heading",
        text: "传说里的关键节点",
      },
      {
        type: "list",
        items: [
          "列车按计划驶离上一站，调度系统随后失去它的信号。",
          "下一站始终没有等到列车进站，区间检查也没有发现车辆。",
          "事后有说法称该区间被永久封闭，相关记录不再对外公开。",
        ],
      },
      {
        type: "quote",
        text: "在一个连空气都被管理的地方，一列载着上百人的车怎么可能说不见就不见？",
      },
      {
        type: "heading",
        text: "理性的追问",
      },
      {
        type: "paragraph",
        text: "研究者更倾向于把它归入传闻的范畴：地铁系统有严格的定时、道岔和电力记录，一列列车不可能在物理上凭空消失。更可能的版本是，某次夜间检修、信号故障或临时封锁被口述放大，最终长成了一个完整的都市传说。真正未能被证实的，是那些被反复转述的「内部说法」。",
      },
      {
        type: "heading",
        text: "为什么我们愿意相信",
      },
      {
        type: "paragraph",
        text: "因为它发生在我们每天都经过的地方。地铁是城市最熟悉也最不透明的器官，隧道深处的黑，天然适合安放一个关于消失的故事。",
      },
    ],
  },
  {
    slug: "voicemail-from-the-deceased",
    title: "深夜语音留言：号码的主人，三年前就已经离开",
    category: "paranormal",
    excerpt:
      "一条凌晨两点收到的语音，背景里是熟悉的雨声、熟悉的脚步，和一句没说完的话。",
    cover:
      "https://readdy.ai/api/search-image?query=Close%20up%20of%20an%20old%20smartphone%20screen%20glowing%20on%20a%20dark%20wooden%20table%20at%20night%2C%20incoming%20call%20interface%20reflected%20in%20the%20glass%2C%20rain%20streaking%20the%20window%20behind%2C%20deep%20charcoal%20and%20warm%20amber%20palette%2C%20quiet%20melancholic%20cinematic%20mood%2C%20no%20people%20visible&width=1200&height=800&seq=lieqi-voicemail-01&orientation=landscape",
    author: "白噪点",
    date: "2026-08-03",
    readTime: "5 分钟",
    tags: ["读者投稿", "通讯记录", "夜间体验"],
    views: 28940,
    blocks: [
      {
        type: "paragraph",
        text: "这是一则来自读者的口述记录，主角是一位普通上班族，内容平淡到几乎不像一个「灵异故事」，却又因为太平淡而让人后背发凉。",
      },
      {
        type: "heading",
        text: "口述原文节选",
      },
      {
        type: "quote",
        text: "凌晨两点，手机响了。屏幕上显示的备注，是一个三年前就已经过世的人。我没敢接，等它自动转成了语音留言。",
      },
      {
        type: "list",
        items: [
          "留言只有七秒，背景里能听到雨声和脚步声，很轻。",
          "有一句没说完的话，像是被谁从中间按下了停止。",
          "号码在第二天回拨时，显示为「该号码已停止服务」。",
        ],
      },
      {
        type: "heading",
        text: "可能的解释",
      },
      {
        type: "paragraph",
        text: "通讯工程师给出过几种冷静的答案：旧号码被回收后重新分配、运营商语音系统的延迟投递、或是记忆对时间点的重构。它们都能解释一部分，却无法解释那种「雨声和脚步」的质感。我们把它如实记录在这里，不做结论——因为灵异杂谈的意义，本来就不在于给一个终点。",
      },
    ],
  },
  {
    slug: "fourth-tenant-old-house",
    title: "老宅的第四个房客：我们都以为他住在楼上",
    category: "paranormal",
    excerpt:
      "搬进去之前，房东说房子住三个人。住进去之后，每个人都以为楼上还住着一位从不露面的邻居。",
    cover:
      "https://readdy.ai/api/search-image?query=Creaky%20old%20two%20story%20wooden%20house%20at%20night%20with%20a%20single%20lit%20upper%20window%2C%20overgrown%20garden%20and%20bare%20branches%2C%20mist%20around%20the%20porch%2C%20deep%20charcoal%20tones%20with%20warm%20amber%20window%20glow%2C%20unsettling%20documentary%20photograph%2C%20no%20people&width=1200&height=800&seq=lieqi-oldhouse-01&orientation=landscape",
    author: "灯下客",
    date: "2026-08-24",
    readTime: "6 分钟",
    tags: ["合租体验", "老宅", "口述记录"],
    views: 31650,
    blocks: [
      {
        type: "paragraph",
        text: "三个刚毕业的年轻人在城郊合租了一栋老木楼。房东交代得很清楚：楼上不常住人，平时只有他们三个。可从第一周开始，三个人都各自「知道」楼上住着一个人。",
      },
      {
        type: "heading",
        text: "三个人，三套互不相同的记忆",
      },
      {
        type: "list",
        items: [
          "A 说凌晨常听到楼上有人挪椅子，但楼上堆着房东的旧家具。",
          "B 说信箱里偶尔会出现一封没有收件人的旧信，纸已经泛黄。",
          "C 说楼梯转角处总有一种「刚有人走过」的温度，尤其在夏天。",
        ],
      },
      {
        type: "quote",
        text: "最可怕的不是听到什么，而是三个人都以为对方见过那个人。",
      },
      {
        type: "heading",
        text: "后来他们做了什么",
      },
      {
        type: "paragraph",
        text: "他们一起上楼清点了一次，房间里只有落灰的家具和一扇打不开的窗。之后再没有出现新的异常。也许真的只是老房子在夜里自己发出声音，也许是三个人共同补完了一个不存在的邻居。口述到这一句，讲述者停了一下，说：其实那封信，我们到现在都没敢拆。",
      },
    ],
  },
  {
    slug: "red-room-darkweb-rumor",
    title: "暗网「红房间」传闻：一场持续二十年的恐惧营销",
    category: "darkweb",
    excerpt:
      "付费观看的直播、无法追踪的服务器、永远无法证实的画面。这个传闻之所以长盛不衰，可能恰恰因为它永远无法被证实。",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20dark%20server%20room%20corridor%20with%20rows%20of%20blinking%20indicator%20lights%2C%20deep%20shadow%20and%20heavy%20vignette%2C%20cold%20charcoal%20palette%20broken%20by%20a%20single%20rust%20red%20emergency%20glow%2C%20cyberpunk%20cinematic%20mood%2C%20no%20text%20no%20people&width=1200&height=800&seq=lieqi-redroom-01&orientation=landscape",
    author: "白噪点",
    date: "2026-09-01",
    readTime: "8 分钟",
    tags: ["暗网", "传闻溯源", "信息安全"],
    views: 57230,
    featured: true,
    blocks: [
      {
        type: "paragraph",
        text: "「红房间」几乎是暗网传闻里最出名的一个：据说在某个只有特定链接才能进入的直播间里，有人付费观看一场持续发生的暴力事件。它被讲述的次数，远远超过了它被证实的次数。",
      },
      {
        type: "heading",
        text: "传闻的传播结构",
      },
      {
        type: "list",
        items: [
          "入口不可验证：链接往往失效、跳转或要求先付款，天然阻断了核实。",
          "叙事自带神秘感：越是无法接触，越容易被想象补全。",
          "借真实议题放大：人口贩卖、深度伪造等真实问题被嫁接到传闻上，使其更可信。",
        ],
      },
      {
        type: "quote",
        text: "一个永远无法被证伪的故事，天生就比真相跑得更远。",
      },
      {
        type: "heading",
        text: "我们真正应该关心的",
      },
      {
        type: "paragraph",
        text: "与其追一个可能不存在的直播入口，不如关注那些真实存在、且被传闻掩盖的风险：钓鱼链接、账号盗用、勒索软件、以及对脆弱人群的精准诈骗。这些才是暗网生态里每天都在发生、也每天都在伤害普通人的部分。",
      },
      {
        type: "heading",
        text: "结语：让传闻回到传闻的位置",
      },
      {
        type: "paragraph",
        text: "我们不否认风险的存在，也不打算用「都是假的」来轻飘飘地打发它。更合适的做法，是把传闻放在它该在的位置：一个提醒你保持警惕的故事，而不是一个必须去验证的地址。",
      },
    ],
  },
  {
    slug: "digital-ghost-deleted-faces",
    title: "数字幽灵：被删除的人脸与被循环播放的弹幕",
    category: "darkweb",
    excerpt:
      "有人整理了一批被平台删除的视频。画面没了，声音还在，弹幕还在，像一群不肯离开的观众。",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20glitched%20digital%20video%20frame%20with%20corrupted%20pixel%20blocks%20and%20scanlines%2C%20fragments%20of%20a%20blurred%20human%20silhouette%20dissolving%20into%20static%2C%20dark%20charcoal%20background%20with%20amber%20and%20rust%20data%20streaks%2C%20cyber%20cinematic%20mood%2C%20no%20readable%20text&width=1200&height=800&seq=lieqi-digitalghost-01&orientation=landscape",
    author: "夜航员",
    date: "2026-09-06",
    readTime: "7 分钟",
    tags: ["数据残留", "网络考古", "平台机制"],
    views: 25470,
    blocks: [
      {
        type: "paragraph",
        text: "网络考古爱好者发现，一些被平台判定违规并下架的视频，在第三方存档站点上仍以残缺形态存在：画面丢失，音轨残留，甚至还有缓慢滚动、永不停止的弹幕。",
      },
      {
        type: "heading",
        text: "像幽灵一样的三种残片",
      },
      {
        type: "list",
        items: [
          "只有音轨的版本：画面被裁成纯黑，声音却完整保留下来。",
          "弹幕驱动的空画面：视频本体失效，评论区仍在循环回放旧句。",
          "被反复压缩的缩略图：人脸的细节被算法抹平，只剩一个模糊的轮廓。",
        ],
      },
      {
        type: "quote",
        text: "删除只作用在索引上，数据有时候会留在别的地方继续呼吸。",
      },
      {
        type: "heading",
        text: "这不是灵异，而是机制",
      },
      {
        type: "paragraph",
        text: "平台的下架通常只移除访问入口，边缘缓存、第三方镜像和用户本地副本都可能继续存活。当这些残留被重新组合，就会产生「数字幽灵」的观感。它更像一面镜子，照出的是我们对删除的误解——在互联网上，删除从来不等于消失。",
      },
    ],
  },
  {
    slug: "sky-city-mirage-time-crack",
    title: "天空出现一座旧城：是海市蜃楼，还是时间的裂缝",
    category: "supernatural",
    excerpt:
      "不同城市的人拍到过同一个画面：云层之上出现倒悬的建筑轮廓，持续几分钟，然后消散。",
    cover:
      "https://readdy.ai/api/search-image?query=Surreal%20distant%20city%20skyline%20upside%20down%20floating%20above%20a%20foggy%20coastal%20horizon%2C%20pale%20layered%20clouds%20and%20amber%20mist%2C%20muted%20charcoal%20blue%20grey%20palette%20with%20warm%20gold%20light%2C%20dreamlike%20atmospheric%20photograph%2C%20no%20people%20no%20text&width=1200&height=800&seq=lieqi-skycity-01&orientation=landscape",
    author: "白噪点",
    date: "2026-09-08",
    readTime: "6 分钟",
    tags: ["海市蜃楼", "大气光学", "视觉错觉"],
    views: 22310,
    blocks: [
      {
        type: "paragraph",
        text: "每隔一段时间，总会有人拍下这样的画面：云层之上浮起一座倒悬的城市，街道、塔尖、窗格清晰得不像幻觉。视频往往只有几十秒，随后一切消散。",
      },
      {
        type: "heading",
        text: "上蜃景：被大气折射压缩的城市",
      },
      {
        type: "paragraph",
        text: "气象学称之为「上蜃景」。当暖空气层压在冷空气之上，光线在两层之间发生强烈折射，远处的城市会被拉伸、倒置并托举到海平面上方。它不是超自然，而是大气在特定温湿条件下做的一次光学实验。",
      },
      {
        type: "quote",
        text: "它真实地存在过，只是它的原乡在几十公里之外，而不是天上。",
      },
      {
        type: "heading",
        text: "为什么总有人说是「旧城」",
      },
      {
        type: "list",
        items: [
          "蜃景中的建筑被大气扭曲后，会显得比现实中更古老、更斑驳。",
          "拍摄者常在海边或高地，远方的现代楼群与旧城区混杂在一起。",
          "记忆会自动把模糊画面补全成「没见过的地方」，从而强化神秘感。",
        ],
      },
      {
        type: "heading",
        text: "我个人的看法",
      },
      {
        type: "paragraph",
        text: "知道原理，并不会让天上那座城变得无趣。恰恰相反，知道它来自几十公里外的真实建筑、经过空气的折射被抬高到云端，反而更让人着迷——自然本身就是最好的创作者。",
      },
    ],
  },
];

export const getArticle = (slug: string): Article | undefined =>
  articles.find((item) => item.slug === slug);

export const getArticlesByCategory = (slug: string): Article[] =>
  articles.filter((item) => item.category === slug);